import React, { useState, useEffect } from "react";
import {
  Box,
  Text,
  Grid,
  Card,
  Group,
  Stack,
  ThemeIcon,
  Progress,
  Tabs,
  Badge,
  RingProgress,
  Tooltip,
  Center,
  SimpleGrid,
  Paper,
  Title,
  Alert,
} from "@mantine/core";
import {
  IconShoppingCart,
  IconBrain,
  IconDeviceTv,
  IconFlame,
  IconServer,
  IconChartScatter,
  IconArrowUpRight,
  IconArrowDownRight,
  IconUser,
  IconBuildingStore,
  IconChartBar,
  IconShieldCheck,
  IconMovie,
  IconShirt,
  IconSpeakerphone,
  IconSparkles,
  IconPlugConnected,
  IconInfoCircle,
  IconTrendingUp,
  IconUsers,
  IconClock,
  IconChecklist,
  IconRuler,
  IconDeviceGamepad2,
  IconDevices,
  IconCompass,
  IconMapPin,
} from "@tabler/icons-react";
import IndiaReachSection from "./IndiaReachSection";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
  AreaChart,
  Area,
  PieChart,
  Pie,
  ReferenceLine,
  Cell,
  ComposedChart,
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ReferenceArea,
  FunnelChart,
  Funnel,
  LabelList,
  Sankey,
  Rectangle,
  Layer,
} from "recharts";
import { API_BASE_URL } from "../../config/baseUrls";

// ==========================================
// MOCK DATA (Based on your Tracking Doc)
// ==========================================

// ==========================================
// BUYER PERSONA MOCK DATA
// Sample data for the 3 approved B2B deliverables (IP Licensors, Merch
// Brands, Non-Endemic Sponsors). Replace with live analytics feeds.
// ==========================================

// --- Persona 1: Global Anime IP Licensors & Streaming Platforms ---
// Sankey node order drives both color and label-anchor side below, so keep
// the 3 genre / 2 interaction / 3 series grouping if you edit this.
const SANKEY_NODE_COLORS = [
  "#EF4444",
  "#3B82F6",
  "#8B5CF6", // genres: Shonen, Isekai, Dark Fantasy
  "#F59E0B",
  "#0EA5E9", // interaction: Watch, Read
  "#EC4899",
  "#6366F1",
  "#14B8A6", // hyped upcoming series
];

const MOCK_IP_MIGRATION_SANKEY = {
  nodes: [
    { name: "Shonen" },
    { name: "Isekai" },
    { name: "Dark Fantasy" },
    { name: "Watch" },
    { name: "Read (Manga)" },
    { name: "Chainsaw Man S2" },
    { name: "Solo Leveling S2" },
    { name: "Frieren S2" },
  ],
  links: [
    { source: 0, target: 3, value: 8500 },
    { source: 0, target: 4, value: 3200 },
    { source: 1, target: 3, value: 5200 },
    { source: 1, target: 4, value: 4100 },
    { source: 2, target: 3, value: 3400 },
    { source: 2, target: 4, value: 2600 },
    { source: 3, target: 5, value: 4200 },
    { source: 3, target: 6, value: 6800 },
    { source: 3, target: 7, value: 3900 },
    { source: 4, target: 5, value: 1800 },
    { source: 4, target: 6, value: 2600 },
    { source: 4, target: 7, value: 2100 },
  ],
};

// --- Persona 2: Merchandise Brands & Apparel Retailers ---
const MOCK_SPENDING_COHORT = [
  {
    fandom: "Evangelion",
    workshops: 88,
    merchStalls: 72,
    premiumTickets: 95,
    avgSpend: 2650,
  },
  {
    fandom: "Demon Slayer",
    workshops: 55,
    merchStalls: 90,
    premiumTickets: 50,
    avgSpend: 1400,
  },
  {
    fandom: "Genshin Impact",
    workshops: 62,
    merchStalls: 78,
    premiumTickets: 68,
    avgSpend: 1850,
  },
  {
    fandom: "Naruto",
    workshops: 45,
    merchStalls: 60,
    premiumTickets: 30,
    avgSpend: 850,
  },
  {
    fandom: "One Piece",
    workshops: 40,
    merchStalls: 55,
    premiumTickets: 25,
    avgSpend: 700,
  },
];

const getSpendHeatColor = (value) => {
  if (value >= 85) return "#6D28D9";
  if (value >= 65) return "#8B5CF6";
  if (value >= 45) return "#C4B5FD";
  return "#EDE9FE";
};

// --- Persona 3: Non-Endemic Event Sponsors ---
const MOCK_ACTIVATION_FUNNEL = [
  { name: "Total Attendees", value: 6400, fill: "#94A3B8" },
  { name: "Scanned QR at Tech/FMCG Stalls", value: 2180, fill: "#60A5FA" },
  { name: "Attended Paid Gaming/Tech Workshops", value: 740, fill: "#2563EB" },
];

const MOCK_FOOTFALL_FORECAST = [
  { time: "10am", scans: 30 },
  { time: "11am", scans: 65 },
  { time: "12pm", scans: 105 },
  { time: "1pm", scans: 145 },
  { time: "2pm", scans: 210 },
  { time: "3pm", scans: 340 },
  { time: "4pm", scans: 395 },
  { time: "5pm", scans: 360 },
  { time: "6pm", scans: 230 },
  { time: "7pm", scans: 125 },
];

// ==========================================
// ONBOARDING SIGNALS MOCK DATA
// Mirrors the 5 live onboarding MCQs seeded in
// Ota-BE/scripts/seedOnboardingQuestions.js — sample response distributions
// only, the backend has no aggregation endpoint yet.
// ==========================================

const MOCK_ONBOARDING_TOTAL_RESPONSES = 12480;

const MOCK_ONBOARDING_MERCH_STYLE = [
  { label: "Loud & Graphic", value: 34 },
  { label: "Subtle Streetwear", value: 28 },
  { label: "Desk Collectibles & Figures", value: 24 },
  { label: "Full Cosplay", value: 14 },
];

const MOCK_ONBOARDING_TSHIRT_SIZE = [
  { label: "M", value: 28 },
  { label: "L", value: 26 },
  { label: "XL", value: 16 },
  { label: "S", value: 14 },
  { label: "XXL", value: 8 },
  { label: "XS", value: 4 },
  { label: "3XL", value: 4 },
];

const MOCK_ONBOARDING_GAMING_GENRE = [
  { label: "Gacha / RPG (e.g. Genshin)", value: 38 },
  { label: "Mobile Esports (e.g. BGMI)", value: 27 },
  { label: "Tactical Shooters (e.g. Valorant)", value: 21 },
  { label: "Console AAA", value: 14 },
];

const MOCK_ONBOARDING_GAMING_PLATFORM = [
  { label: "Mobile", value: 42 },
  { label: "PC", value: 31 },
  { label: "PlayStation", value: 18 },
  { label: "Nintendo Switch", value: 9 },
];

const MOCK_ONBOARDING_EVENT_QUEST = [
  { label: "Exclusive Merch Drops", value: 36 },
  { label: "Meeting Creators & Voice Actors", value: 29 },
  { label: "Cosplay Meetups", value: 20 },
  { label: "Workshops & Panels", value: 15 },
];

// Platform x Genre cross-tab — % of each platform's users who name that
// genre as their most-played (rows sum to ~100).
const MOCK_ONBOARDING_PLATFORM_GENRE_CROSS = [
  {
    platform: "PC",
    gachaRpg: 22,
    shooters: 38,
    mobileEsports: 8,
    consoleAAA: 32,
  },
  {
    platform: "PlayStation",
    gachaRpg: 18,
    shooters: 14,
    mobileEsports: 4,
    consoleAAA: 64,
  },
  {
    platform: "Mobile",
    gachaRpg: 58,
    shooters: 10,
    mobileEsports: 30,
    consoleAAA: 2,
  },
  {
    platform: "Nintendo Switch",
    gachaRpg: 40,
    shooters: 6,
    mobileEsports: 4,
    consoleAAA: 50,
  },
];

const getCrossTabHeatColor = (value) => {
  if (value >= 50) return "#1D4ED8";
  if (value >= 30) return "#3B82F6";
  if (value >= 15) return "#93C5FD";
  return "#DBEAFE";
};

const MOCK_SHOP_FUNNEL = [
  { name: "Shop Views", value: 15000, fill: "#818cf8" },
  { name: "Product Clicks", value: 8500, fill: "#6366f1" },
  { name: "Add to Cart", value: 3200, fill: "#4f46e5" },
  { name: "Checkout", value: 1800, fill: "#4338ca" },
  { name: "Purchases", value: 1100, fill: "#3730a3" },
];

const MOCK_REEL_RETENTION = [
  { percent: "0%", viewers: 10000 },
  { percent: "25%", viewers: 7500 },
  { percent: "50%", viewers: 5200 },
  { percent: "75%", viewers: 3100 },
  { percent: "100%", viewers: 1800 },
];

const MOCK_QUIZ_STATS = [
  { name: "Pop Culture", avgScore: 85, completions: 4200 },
  { name: "Tech History", avgScore: 62, completions: 3100 },
  { name: "Mega Quiz #1", avgScore: 41, completions: 8900 },
  { name: "Anime Trivia", avgScore: 78, completions: 5500 },
];
const MOCK_CONTENT_ENGAGEMENT = [
  { name: "Reels", views: 45000, engRate: 17.7 },
  { name: "Posts", views: 25000, engRate: 12.0 },
  { name: "Blogs", views: 12000, engRate: 8.5 },
];

const MOCK_TOP_IDEAS = [
  { name: "Oversized Cyber Jacket", votes: 8500 },
  { name: "Tokyo Pop-up Event", votes: 6200 },
  { name: "Neon Beanie Colors", votes: 4100 },
  { name: "Vintage Wash Tees", votes: 3800 },
  { name: "Enamel Pin Set", votes: 2100 },
];
const maxVotes = Math.max(...MOCK_TOP_IDEAS.map((i) => i.votes));

const MOCK_CATEGORY_INTEREST = [
  { name: "Apparel", value: 55 },
  { name: "Events", value: 30 },
  { name: "Accessories", value: 15 },
];

const PIE_COLORS = ["#4F46E5", "#F59E0B", "#10B981"];

const COLORS = ["#4F46E5", "#10B981", "#F59E0B", "#EF4444"];

const MOCK_PROFILE_SECTIONS = [
  { subject: "My Tickets", views: 12500, fullMark: 15000 },
  { subject: "My Orders", views: 9800, fullMark: 15000 },
  { subject: "My Votes", views: 5600, fullMark: 15000 },
  { subject: "My Ideas", views: 3200, fullMark: 15000 },
  { subject: "Settings", views: 4100, fullMark: 15000 },
];

const MOCK_TICKET_OPENS = [
  { time: "7 Days Prior", opens: 120 },
  { time: "3 Days Prior", opens: 450 },
  { time: "1 Day Prior", opens: 1800 },
  { time: "Day Of Event", opens: 8500 },
];

const MOCK_REPORTS = [
  { reason: "Spam/Bot", count: 340 },
  { reason: "Inappropriate", count: 210 },
  { reason: "Harassment", count: 85 },
];

// ==========================================
// COMPONENTS
// ==========================================

const KpiCard = ({ title, value, trend, isPositive, icon, color }) => (
  <Paper withBorder p="md" radius="md">
    <Group justify="space-between">
      <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
        {title}
      </Text>
      <ThemeIcon color={color} variant="light" size="sm">
        {icon}
      </ThemeIcon>
    </Group>
    <Group align="flex-end" gap="xs" mt={25}>
      <Text size="xl" fw={700}>
        {value}
      </Text>
      {trend != null && (
        <Text c={isPositive ? "teal" : "red"} fz="sm" fw={500}>
          <span>
            {isPositive ? "+" : "-"}
            {trend}%
          </span>
          {isPositive ? (
            <IconArrowUpRight size={14} style={{ marginBottom: -2 }} />
          ) : (
            <IconArrowDownRight size={14} style={{ marginBottom: -2 }} />
          )}
        </Text>
      )}
    </Group>
  </Paper>
);

// Big-number teaser card used for the "Why they pay" stat call-outs in each
// buyer-persona section.
const PersonaStatCard = ({ icon, color, eyebrow, stat, description }) => (
  <Card withBorder radius="md" p="xl" h="100%">
    <Group gap="xs" mb="md">
      <ThemeIcon color={color} variant="light" radius="xl">
        {icon}
      </ThemeIcon>
      <Text fw={700} size="sm" c="dimmed" tt="uppercase">
        {eyebrow}
      </Text>
    </Group>
    <Text fw={900} size="2.2rem" className="ota-gradient-text" lh={1.1}>
      {stat}
    </Text>
    <Text size="sm" c="dimmed" mt="sm">
      {description}
    </Text>
  </Card>
);

// AI-driven insight card (collaborative filtering / look-alike modeling /
// spatial forecasting) called out in the approved plan for each persona.
const AIInsightCard = ({ title, model, color = "grape", children }) => (
  <Card withBorder radius="md" p="xl" h="100%">
    <Group gap="xs" mb="xs">
      <ThemeIcon color={color} variant="light" radius="xl">
        <IconSparkles size={16} />
      </ThemeIcon>
      <Text fw={700} size="sm">
        {title}
      </Text>
      <Badge color={color} variant="light" size="xs" ml="auto">
        {model}
      </Badge>
    </Group>
    <Text size="sm" c="dimmed">
      {children}
    </Text>
  </Card>
);

// POS-dependent insight card — flags a deliverable that goes live only once
// the point-of-sale integration ships.
const PosDependentInsightCard = ({ title, children }) => (
  <Card
    withBorder
    radius="md"
    p="xl"
    h="100%"
    style={{ borderStyle: "dashed" }}
  >
    <Group gap="xs" mb="xs">
      <ThemeIcon color="gray" variant="light" radius="xl">
        <IconPlugConnected size={16} />
      </ThemeIcon>
      <Text fw={700} size="sm">
        {title}
      </Text>
      {/* <Badge color="gray" variant="outline" size="xs" ml="auto">
        Requires POS Integration
      </Badge> */}
    </Group>
    <Text size="sm" c="dimmed">
      {children}
    </Text>
  </Card>
);

// Custom Sankey node renderer: colors + labels each node, anchoring the
// label text on whichever side keeps it inside the chart (left column
// labels render to the right of the node, right column labels to the left).
const SankeyNode = ({ x, y, width, height, index, payload }) => {
  const isLastColumn = index >= 5;
  const textAnchor = isLastColumn ? "end" : "start";
  const textX = isLastColumn ? x - 8 : x + width + 8;
  return (
    <Layer key={`sankey-node-${index}`}>
      <Rectangle
        x={x}
        y={y}
        width={width}
        height={height}
        fill={SANKEY_NODE_COLORS[index % SANKEY_NODE_COLORS.length]}
        fillOpacity={0.9}
      />
      <text
        x={textX}
        y={y + height / 2}
        textAnchor={textAnchor}
        dominantBaseline="middle"
        fontSize={12}
        fontWeight={600}
        fill="#374151"
      >
        {payload.name}
      </text>
    </Layer>
  );
};

// Fandom x spend-channel cohort heatmap grid (same visual language as the
// legacy "Anime Type by Age" grid elsewhere in this file).
const SpendCohortHeatmap = ({ data }) => (
  <Box
    style={{
      display: "grid",
      gridTemplateColumns: "1.2fr repeat(3, 1fr) 1fr",
      gap: 10,
      alignItems: "center",
    }}
  >
    <Text fw={700} size="sm">
      Fandom
    </Text>
    <Text fw={700} size="sm">
      Workshops
    </Text>
    <Text fw={700} size="sm">
      Merch Stalls
    </Text>
    <Text fw={700} size="sm">
      Premium Tickets
    </Text>
    <Text fw={700} size="sm">
      Avg Spend / Attendee
    </Text>
    {data.map((row) => (
      <React.Fragment key={row.fandom}>
        <Text fw={600} size="sm">
          {row.fandom}
        </Text>
        <Badge
          radius="sm"
          size="lg"
          fullWidth
          styles={{ root: { color: row.workshops >= 65 ? "#fff" : "#4C1D95" } }}
          style={{ backgroundColor: getSpendHeatColor(row.workshops) }}
        >
          {row.workshops}
        </Badge>
        <Badge
          radius="sm"
          size="lg"
          fullWidth
          styles={{
            root: { color: row.merchStalls >= 65 ? "#fff" : "#4C1D95" },
          }}
          style={{ backgroundColor: getSpendHeatColor(row.merchStalls) }}
        >
          {row.merchStalls}
        </Badge>
        <Badge
          radius="sm"
          size="lg"
          fullWidth
          styles={{
            root: { color: row.premiumTickets >= 65 ? "#fff" : "#4C1D95" },
          }}
          style={{ backgroundColor: getSpendHeatColor(row.premiumTickets) }}
        >
          {row.premiumTickets}
        </Badge>
        <Text fw={700} size="sm" ta="center">
          ₹{row.avgSpend.toLocaleString("en-IN")}
        </Text>
      </React.Fragment>
    ))}
  </Box>
);

// Horizontal % bar chart used for each onboarding-question distribution.
const OnboardingBarChart = ({ data, color, height = 220 }) => (
  <Box h={height}>
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={data}
        layout="vertical"
        margin={{ top: 5, right: 30, bottom: 5, left: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
        <XAxis
          type="number"
          tickFormatter={(v) => `${v}%`}
          tick={{ fontSize: 11 }}
          domain={[0, "dataMax + 10"]}
        />
        <YAxis
          type="category"
          dataKey="label"
          width={150}
          tick={{ fontSize: 11 }}
        />
        <RechartsTooltip
          formatter={(v) => [`${v}%`, "Share of responses"]}
          cursor={{ fill: "#f3f4f6" }}
        />
        <Bar dataKey="value" fill={color} radius={[0, 4, 4, 0]} barSize={16}>
          <LabelList
            dataKey="value"
            position="right"
            formatter={(v) => `${v}%`}
            style={{ fontSize: 11, fontWeight: 600, fill: "#374151" }}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  </Box>
);

// One onboarding question = one card: the exact question text as asked at
// signup, its answer distribution, and which buyer persona it feeds.
const OnboardingQuestionCard = ({
  icon,
  color,
  question,
  feedsPersona,
  data,
  height,
}) => (
  <Card withBorder radius="md" p="xl" h="100%">
    <Group justify="space-between" align="flex-start" mb="md">
      <Group gap="xs" wrap="nowrap">
        <ThemeIcon color={color} variant="light" radius="xl">
          {icon}
        </ThemeIcon>
        <Text fw={700} size="sm">
          {question}
        </Text>
      </Group>
      <Badge color={color} variant="light" size="xs">
        {feedsPersona}
      </Badge>
    </Group>
    <OnboardingBarChart
      data={data}
      color={`var(--mantine-color-${color}-6)`}
      height={height}
    />
  </Card>
);

// Fandom-agnostic cross-tab grid: gaming platform x genre, same visual
// language as the Spending Velocity heatmap above.
const PlatformGenreCrossTab = ({ data }) => (
  <Box
    style={{
      display: "grid",
      gridTemplateColumns: "1.1fr repeat(4, 1fr)",
      gap: 10,
      alignItems: "center",
    }}
  >
    <Text fw={700} size="sm">
      Platform
    </Text>
    <Text fw={700} size="sm">
      Gacha/RPG
    </Text>
    <Text fw={700} size="sm">
      Shooters
    </Text>
    <Text fw={700} size="sm">
      Mobile Esports
    </Text>
    <Text fw={700} size="sm">
      Console AAA
    </Text>
    {data.map((row) => (
      <React.Fragment key={row.platform}>
        <Text fw={600} size="sm">
          {row.platform}
        </Text>
        {[row.gachaRpg, row.shooters, row.mobileEsports, row.consoleAAA].map(
          (value, idx) => (
            <Badge
              key={idx}
              radius="sm"
              size="lg"
              fullWidth
              styles={{ root: { color: value >= 50 ? "#fff" : "#1E3A8A" } }}
              style={{ backgroundColor: getCrossTabHeatColor(value) }}
            >
              {value}%
            </Badge>
          ),
        )}
      </React.Fragment>
    ))}
  </Box>
);

const DEMO_DATA = {
  demographics: [
    { name: "Girls 18-24", value: 32, color: "#3b66e0" },
    { name: "Boys 18-24", value: 28, color: "#1fb6d8" },
    { name: "Girls 25-34", value: 18, color: "#9277f0" },
    { name: "Boys 25-34", value: 14, color: "#e0654a" },
    { name: "Others", value: 8, color: "#9aa5a5" },
  ],
  hourly: [
    { time: "11a", scans: 40 },
    { time: "12p", scans: 75 },
    { time: "1p", scans: 110 },
    { time: "2p", scans: 150 },
    { time: "3p", scans: 168 },
    { time: "4p", scans: 142 },
  ],
  funnel: [
    { stage: "Scanned QR", val: 1000, pct: 100 },
    { stage: "Claimed Reward", val: 640, pct: 64 },
    { stage: "Kept Playing", val: 310, pct: 31 },
  ],
  leads: [
    { name: "Hot Leads", val: 180, color: "red" },
    { name: "Warm Leads", val: 340, color: "blue" },
    { name: "Cold Leads", val: 220, color: "gray" },
  ],
  animeHeatmap: [
    { genre: "Shonen", g1: 82, g2: 90, g3: 55, g4: 30 },
    { genre: "Isekai", g1: 40, g2: 78, g3: 68, g4: 35 },
    { genre: "Slice of Life", g1: 35, g2: 60, g3: 72, g4: 58 },
  ],
};

// --- SUB-COMPONENT: EVENT ANALYTICS ---
const EventAnalyticsView = () => (
  <Stack gap="xl">
    <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
      {/* 1. Demographics */}
      <Paper withBorder p="xl" radius="md">
        <Title order={4} mb="xs">
          Visitor Demographics
        </Title>
        <Box h={250}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={DEMO_DATA.demographics}
                innerRadius={60}
                outerRadius={80}
                dataKey="value"
              >
                {DEMO_DATA.demographics.map((entry, index) => (
                  <Cell key={index} fill={entry.color} />
                ))}
              </Pie>
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Box>
      </Paper>

      {/* 2. Traffic Bar */}
      <Paper withBorder p="xl" radius="md">
        <Title order={4} mb="xs">
          Traffic by Hour
        </Title>
        <Box h={250}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={DEMO_DATA.hourly}>
              <XAxis dataKey="time" />
              <Tooltip />
              <Bar dataKey="scans" fill="#3b66e0" />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Paper>
    </SimpleGrid>

    {/* 3. Heatmap Grid */}
    <Paper withBorder p="xl" radius="md">
      <Title order={4} mb="md">
        Anime Type by Age
      </Title>
      <Box
        style={{
          display: "grid",
          gridTemplateColumns: "100px repeat(4, 1fr)",
          gap: 10,
        }}
      >
        <Text fw={700}>Genre</Text>
        <Text fw={700}>13-17</Text>
        <Text fw={700}>18-24</Text>
        <Text fw={700}>25-34</Text>
        <Text fw={700}>35+</Text>
        {DEMO_DATA.animeHeatmap.map((row) => (
          <React.Fragment key={row.genre}>
            <Text fw={600}>{row.genre}</Text>
            <Badge color="blue" variant="light">
              {row.g1}
            </Badge>
            <Badge color="blue" variant="filled">
              {row.g2}
            </Badge>
            <Badge color="blue" variant="light">
              {row.g3}
            </Badge>
            <Badge color="gray" variant="light">
              {row.g4}
            </Badge>
          </React.Fragment>
        ))}
      </Box>
    </Paper>
  </Stack>
);

// Investor-demo banner: conveys data density (chart count, domains, personas,
// onboarding coverage) at a glance instead of making a viewer count tabs.
const InsightStatTile = ({ value, label }) => (
  <div>
    <Text fw={900} size="1.6rem" c="white" lh={1.1}>
      {value}
    </Text>
    <Text
      size="xs"
      tt="uppercase"
      fw={600}
      style={{ color: "rgba(255,255,255,0.8)" }}
    >
      {label}
    </Text>
  </div>
);

const InsightDensityBanner = ({ onboardingTotal }) => (
  <Paper
    radius="lg"
    p="xl"
    style={{
      background:
        "linear-gradient(90deg, #07b9ce -14.02%, #3969e7 36.11%, #7d2ae7 105.67%)",
    }}
  >
    <Group justify="space-between" align="center" wrap="wrap" gap="xl">
      <div>
        <Badge
          variant="light"
          size="sm"
          mb="xs"
          style={{ backgroundColor: "rgba(255,255,255,0.18)", color: "#fff" }}
        >
          Investor Preview
        </Badge>
        <Text fw={800} size="xl" c="white">
          A Single Source of Commercial Truth
        </Text>
        <Text size="sm" maw={440} style={{ color: "rgba(255,255,255,0.85)" }}>
          Every chart below is built to answer a real buyer's question — not a
          vanity metric.
        </Text>
      </div>
      <Group gap="xl" wrap="wrap">
        <InsightStatTile value="21+" label="Live Visualizations" />
        <InsightStatTile value="7" label="Analytics Domains" />
        <InsightStatTile value="4" label="B2B Buyer Personas" />
        <InsightStatTile
          value={
            onboardingTotal != null
              ? onboardingTotal.toLocaleString("en-IN")
              : "—"
          }
          label="Onboarding Responses"
        />
      </Group>
    </Group>
  </Paper>
);

function MerchantDashboard() {
  return (
    <Box p="md">
      <Title order={1} mb="xl">
        Merchant Dashboard
      </Title>
      <Tabs defaultValue="overview">
        <Tabs.List>
          <Tabs.Tab
            value="overview"
            leftSection={<IconBuildingStore size={16} />}
          >
            Merchant Overview
          </Tabs.Tab>
          <Tabs.Tab value="analytics" leftSection={<IconChartBar size={16} />}>
            Event Analytics
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="overview" pt="xl">
          <SimpleGrid cols={{ base: 1, md: 4 }}>
            <Paper withBorder p="md">
              <Text c="dimmed">Total Revenue</Text>
              <Text size="xl" fw={700}>
                ₹1.2M
              </Text>
            </Paper>
            <Paper withBorder p="md">
              <Text c="dimmed">Active Shops</Text>
              <Text size="xl" fw={700}>
                3
              </Text>
            </Paper>
            <Paper withBorder p="md">
              <Text c="dimmed">Total Sales</Text>
              <Text size="xl" fw={700}>
                450
              </Text>
            </Paper>
            <Paper withBorder p="md">
              <Text c="dimmed">Avg Engagement</Text>
              <Text size="xl" fw={700}>
                17%
              </Text>
            </Paper>
          </SimpleGrid>
        </Tabs.Panel>

        <Tabs.Panel value="analytics" pt="xl">
          <EventAnalyticsView />
        </Tabs.Panel>
      </Tabs>
    </Box>
  );
}

const ANALYTICS_BASE_URL = `${API_BASE_URL}`;
const getAdminAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
  "Content-Type": "application/json",
});

export default function AnalyticsDashboard() {
  // Buyer Personas is the primary investor-facing view, so it opens by
  // default rather than the internal ops "commerce" tab.
  const [activeTab, setActiveTab] = useState("oii");

  // Real, DB-backed data for every tab except Content (Feeds) and Buyer
  // Personas (OII) — those need PostHog + AI-generated insights, tracked as
  // future scope. See Ota-BE/Connectors/posthogController.js and
  // onboardingController.js#getOnboardingResponseStats for the queries.
  const [analytics, setAnalytics] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [authError, setAuthError] = useState(false);

  useEffect(() => {
    fetch(`${ANALYTICS_BASE_URL}/analytics/dashboard?duration=all`, {
      headers: getAdminAuthHeaders(),
    })
      .then((r) => r.json())
      .then((res) => {
        if (res.success) {
          setAnalytics(res.data.database);
        } else {
          // Admin sessions last 24h — an expired/missing token 401s here,
          // which previously just left the KPI cards silently blank.
          setAuthError(true);
        }
      })
      .catch(() => setAuthError(true));

    fetch(`${ANALYTICS_BASE_URL}/admin/onboarding-questions/stats`, {
      headers: getAdminAuthHeaders(),
    })
      .then((r) => r.json())
      .then((res) => {
        if (res.success) setOnboarding(res.data);
      })
      .catch(console.error);
  }, []);

  return (
    <Box className="space-y-6 animate-fade-in" pb={50}>
      {authError && (
        <Alert
          icon={<IconInfoCircle size={16} />}
          color="red"
          variant="light"
          radius="md"
        >
          Couldn't load live analytics — your admin session has likely expired
          (sessions last 24 hours).{" "}
          <a href="/admin" style={{ fontWeight: 700 }}>
            Log in again
          </a>{" "}
          to refresh it. Charts below are showing placeholder data until then.
        </Alert>
      )}

      <div>
        <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
          Analytics & Insights
        </h1>
        <Text c="dimmed">
          Deep dive into user behavior and platform performance.
        </Text>
      </div>

      <InsightDensityBanner onboardingTotal={onboarding?.totalResponders} />

      {/* TOP LEVEL KPIs — Revenue & Quiz Score are real; Reel Completion and
          System Latency have no tracking source yet (future scope: PostHog
          + APM), so they stay illustrative until that's wired up. */}
      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
        <KpiCard
          title="Total Revenue"
          value={
            analytics
              ? `₹${Number(analytics.revenue).toLocaleString("en-IN")}`
              : "—"
          }
          trend={null}
          isPositive={true}
          icon={<IconShoppingCart size={16} />}
          color="blue"
        />
        <KpiCard
          title="Avg Quiz Accuracy"
          value={
            analytics?.avgQuizAccuracy != null
              ? `${analytics.avgQuizAccuracy}%`
              : "—"
          }
          trend={null}
          isPositive={true}
          icon={<IconBrain size={16} />}
          color="grape"
        />
        <KpiCard
          title="Reel Completion"
          value="18%"
          trend={4.2}
          isPositive={false}
          icon={<IconDeviceTv size={16} />}
          color="teal"
        />
        <KpiCard
          title="System Latency"
          value="124ms"
          trend={1.8}
          isPositive={false}
          icon={<IconServer size={16} />}
          color="red"
        />
      </SimpleGrid>

      <Tabs
        value={activeTab}
        onChange={setActiveTab}
        variant="pills"
        radius="xl"
        mt="xl"
        styles={{
          tab: {
            fontWeight: 600,
            padding: "10px 18px",
          },
        }}
      >
        <Paper
          withBorder
          radius="xl"
          p={6}
          mb="xl"
          style={{ display: "inline-block", maxWidth: "100%" }}
        >
          <Tabs.List style={{ flexWrap: "wrap", rowGap: 6 }}>
            <Tabs.Tab
              value="commerce"
              leftSection={<IconShoppingCart size={16} />}
            >
              Shop & Commerce
            </Tabs.Tab>
            <Tabs.Tab value="content" leftSection={<IconDeviceTv size={16} />}>
              Content (Feeds)
            </Tabs.Tab>
            <Tabs.Tab
              value="gamification"
              leftSection={<IconBrain size={16} />}
            >
              Gamification (Quiz)
            </Tabs.Tab>
            <Tabs.Tab value="community" leftSection={<IconFlame size={16} />}>
              Community (Voting)
            </Tabs.Tab>
            <Tabs.Tab value="profile" leftSection={<IconUser size={16} />}>
              Profile & Safety
            </Tabs.Tab>
            <Tabs.Tab value="geography" leftSection={<IconMapPin size={16} />}>
              Geographic Reach
            </Tabs.Tab>
            <Tabs.Tab
              value="oii"
              leftSection={<IconChartScatter size={16} />}
              rightSection={
                <Badge size="xs" variant="filled" color="yellow" c="dark">
                  Start Here
                </Badge>
              }
            >
              Buyer Personas (B2B)
            </Tabs.Tab>
          </Tabs.List>
        </Paper>

        {/* ==================== COMMERCE TAB ==================== */}
        <Tabs.Panel value="commerce" pt="xl">
          <Grid>
            <Grid.Col span={{ base: 12, md: 8 }}>
              <Card withBorder radius="md" p="xl" h="100%">
                <Text fw={700} mb="lg">
                  Conversion Funnel
                </Text>
                <Text size="sm" c="dimmed" mb="xl">
                  QR scans across all shops → orders started → orders paid.
                  ("Product Clicks"/"Add to Cart" aren't trackable yet — no
                  event log for those.)
                </Text>
                <Box h={350}>
                  <ResponsiveContainer width="100%" height="100%">
                    <FunnelChart>
                      <RechartsTooltip cursor={{ fill: "transparent" }} />
                      <Funnel
                        dataKey="value"
                        data={
                          analytics?.commerceFunnel
                            ? [
                                {
                                  name: "Scanned Shop QR",
                                  value: analytics.commerceFunnel.scanned,
                                  fill: "#818cf8",
                                },
                                {
                                  name: "Orders Started",
                                  value:
                                    analytics.commerceFunnel.ordersInitiated,
                                  fill: "#6366f1",
                                },
                                {
                                  name: "Orders Paid",
                                  value: analytics.commerceFunnel.ordersPaid,
                                  fill: "#4338ca",
                                },
                              ]
                            : MOCK_SHOP_FUNNEL
                        }
                        isAnimationActive
                      >
                        <LabelList
                          position="right"
                          fill="#000"
                          stroke="none"
                          dataKey="name"
                        />
                        <LabelList
                          position="center"
                          fill="#fff"
                          stroke="none"
                          dataKey="value"
                          fontWeight="bold"
                        />
                      </Funnel>
                    </FunnelChart>
                  </ResponsiveContainer>
                </Box>
              </Card>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Stack h="100%">
                <Card withBorder radius="md" p="xl" className="flex-1">
                  <Text fw={700} mb="xs">
                    Payment Success Rate
                  </Text>
                  <Center h={150}>
                    <RingProgress
                      size={140}
                      thickness={14}
                      roundCaps
                      sections={[
                        {
                          value: analytics?.paymentSuccessRate ?? 0,
                          color: "teal",
                        },
                      ]}
                      label={
                        <Text ta="center" size="xl" fw={700}>
                          {analytics?.paymentSuccessRate != null
                            ? `${analytics.paymentSuccessRate}%`
                            : "—"}
                        </Text>
                      }
                    />
                  </Center>
                  <Text size="sm" c="dimmed" ta="center">
                    {analytics?.paymentSuccessRate != null
                      ? `${100 - analytics.paymentSuccessRate}% of initiated checkouts fail at the payment gateway.`
                      : "No paid/failed orders yet."}
                  </Text>
                </Card>
                <Card withBorder radius="md" p="xl" className="flex-1">
                  <Text fw={700} mb="sm">
                    Top Search Queries (Shop)
                  </Text>
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text size="sm">1. "Ota Oversized"</Text>
                      <Badge variant="light">1.2k</Badge>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm">2. "Event VIP"</Text>
                      <Badge variant="light">850</Badge>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm">3. "Cyber Shades"</Text>
                      <Badge variant="light">620</Badge>
                    </Group>
                  </Stack>
                </Card>
              </Stack>
            </Grid.Col>
          </Grid>
        </Tabs.Panel>

        {/* ==================== CONTENT TAB ==================== */}
        <Tabs.Panel value="content" pt="xl">
          <Grid>
            <Grid.Col span={{ base: 12, lg: 6 }}>
              <Card withBorder radius="md" p="xl" h="100%">
                <Text fw={700} mb="md">
                  Reel Watch Completion Drop-off
                </Text>
                <Text size="sm" c="dimmed" mb="xl">
                  Measures where users scroll away from video content.
                </Text>
                <Box h={300}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={MOCK_REEL_RETENTION}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="percent" />
                      <YAxis />
                      <RechartsTooltip />
                      <Area
                        type="monotone"
                        dataKey="viewers"
                        stroke="#EF4444"
                        fill="#FEE2E2"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </Card>
            </Grid.Col>
            <Grid.Col span={{ base: 12, lg: 6 }}>
              <Card withBorder radius="md" p="xl" h="100%">
                <Text fw={700} mb="sm">
                  Views vs Engagement Rate
                </Text>
                <Text size="sm" c="dimmed" mb="xl">
                  Are your highest viewed formats actually generating
                  interactions?
                </Text>

                <Box h={300}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={MOCK_CONTENT_ENGAGEMENT}
                      margin={{ top: 10 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" />

                      {/* Left Axis for Views */}
                      <YAxis
                        yAxisId="views"
                        orientation="left"
                        tickFormatter={(v) => `${v / 1000}k`}
                        tick={{ fontSize: 12 }}
                      />
                      {/* Right Axis for % */}
                      <YAxis
                        yAxisId="eng"
                        orientation="right"
                        tickFormatter={(v) => `${v}%`}
                        tick={{ fontSize: 12 }}
                      />

                      <RechartsTooltip cursor={{ fill: "#f3f4f6" }} />
                      <Legend
                        verticalAlign="top"
                        height={36}
                        iconType="circle"
                      />

                      <Bar
                        yAxisId="views"
                        dataKey="views"
                        name="Total Views"
                        fill="#3B82F6"
                        radius={[4, 4, 0, 0]}
                        barSize={35}
                      />
                      <Bar
                        yAxisId="eng"
                        dataKey="engRate"
                        name="Engagement Rate (%)"
                        fill="#10B981"
                        radius={[4, 4, 0, 0]}
                        barSize={35}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Card>
            </Grid.Col>
          </Grid>
        </Tabs.Panel>

        {/* ==================== GAMIFICATION TAB ==================== */}
        <Tabs.Panel value="gamification" pt="xl">
          <Card withBorder radius="md" p="xl">
            <Text fw={700} size="lg" mb="xs">
              Quiz Difficulty & Completion Rate
            </Text>
            <Text size="sm" c="dimmed" mb="xl">
              Compare how many people play a quiz versus how well they score.
            </Text>

            <Box h={400}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={
                    analytics?.quizStats?.length
                      ? analytics.quizStats
                      : MOCK_QUIZ_STATS
                  }
                  margin={{ top: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />

                  {/* Left Axis for Plays */}
                  <YAxis
                    yAxisId="left"
                    tickFormatter={(v) => `${v / 1000}k`}
                    tick={{ fontSize: 12 }}
                  />
                  {/* Right Axis for Scores */}
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    domain={[0, 100]}
                    tickFormatter={(v) => `${v}%`}
                    tick={{ fontSize: 12 }}
                  />

                  <RechartsTooltip cursor={{ fill: "#f3f4f6" }} />
                  <Legend verticalAlign="top" height={36} />

                  <Bar
                    yAxisId="left"
                    dataKey="completions"
                    name="Total Plays"
                    fill="#4F46E5"
                    radius={[4, 4, 0, 0]}
                    barSize={50}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="avgScore"
                    name="Avg Score (%)"
                    stroke="#F59E0B"
                    strokeWidth={4}
                    dot={{ r: 6, fill: "#fff", strokeWidth: 3 }}
                  />

                  {/* The Goal Line */}
                  <ReferenceLine
                    yAxisId="right"
                    y={75}
                    stroke="#10B981"
                    strokeDasharray="3 3"
                    label={{
                      position: "top",
                      value: "Ideal Difficulty (75%)",
                      fill: "#10B981",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </Card>
        </Tabs.Panel>
        {/* ==================== COMMUNITY TAB ==================== */}
        <Tabs.Panel value="community" pt="xl">
          <Grid>
            {/* LEFT: Top Voted Ideas (Mantine Leaderboard) */}
            <Grid.Col span={{ base: 12, md: 8 }}>
              <Card withBorder radius="md" p="xl" h="100%">
                <Group justify="space-between" align="flex-end" mb="xl">
                  <div>
                    <Text fw={700} size="lg">
                      Most Wanted: Top Voted Ideas
                    </Text>
                    <Text size="sm" c="dimmed">
                      The community's most desired products and events.
                    </Text>
                  </div>
                  <Badge color="orange" variant="light" size="lg">
                    Live Rankings
                  </Badge>
                </Group>

                <Stack gap="lg">
                  {(() => {
                    const topIdeas = analytics?.topProjects?.length
                      ? analytics.topProjects.map((p) => ({
                          name: p.title,
                          votes: p.currentVotes,
                        }))
                      : MOCK_TOP_IDEAS;
                    const maxIdeaVotes = Math.max(
                      ...topIdeas.map((i) => i.votes),
                      1,
                    );
                    return topIdeas.map((idea, idx) => (
                      <Box key={idx}>
                        <Group justify="space-between" mb={8}>
                          <Group gap="sm">
                            {/* Gold, Silver, Bronze coloring for top 3 */}
                            <Text
                              fw={900}
                              size="lg"
                              c={
                                idx === 0
                                  ? "yellow.6"
                                  : idx === 1
                                    ? "gray.5"
                                    : idx === 2
                                      ? "orange.8"
                                      : "dimmed"
                              }
                            >
                              #{idx + 1}
                            </Text>
                            <Text fw={600}>{idea.name}</Text>
                          </Group>
                          <Text fw={700}>
                            {idea.votes.toLocaleString()}{" "}
                            <Text span size="xs" c="dimmed" fw={500}>
                              votes
                            </Text>
                          </Text>
                        </Group>
                        {/* Animated fill bar relative to the #1 idea */}
                        <Progress
                          value={(idea.votes / maxIdeaVotes) * 100}
                          color={idx === 0 ? "yellow.5" : "blue.5"}
                          size="xl"
                          radius="xl"
                          striped={idx === 0}
                          animated={idx === 0}
                        />
                      </Box>
                    ));
                  })()}
                </Stack>
              </Card>
            </Grid.Col>

            {/* RIGHT: Funnel & Demographics */}
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Stack h="100%">
                {/* 1. Idea Creation Conversion Funnel */}
                <Card withBorder radius="md" p="xl" className="flex-1">
                  <Text fw={700} mb="sm">
                    Idea Submission Conversion
                  </Text>
                  {(() => {
                    const submitted = analytics?.ideaFunnel?.submitted ?? 1240;
                    const approved = analytics?.ideaFunnel?.approved ?? 485;
                    const pct =
                      submitted > 0
                        ? Math.round((approved / submitted) * 100)
                        : 0;
                    return (
                      <>
                        <Group grow mt="md">
                          <div>
                            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                              Submitted
                            </Text>
                            <Text fw={800} size="xl">
                              {submitted.toLocaleString()}
                            </Text>
                          </div>
                          <div>
                            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                              Approved / Live
                            </Text>
                            <Text fw={800} size="xl" c="teal">
                              {approved.toLocaleString()}
                            </Text>
                          </div>
                        </Group>
                        <Progress
                          value={pct}
                          mt="md"
                          size="lg"
                          radius="xl"
                          color="teal"
                          striped
                          animated
                        />
                        <Text size="xs" c="dimmed" mt="sm" ta="center">
                          <b>{pct}%</b> of submitted ideas get approved and go
                          live.
                        </Text>
                      </>
                    );
                  })()}
                </Card>
                {/* 2. Category Interest Pie Chart */}
                <Card
                  withBorder
                  radius="md"
                  p="xl"
                  className="flex-1"
                  style={{ display: "flex", flexDirection: "column" }}
                >
                  <Text fw={700} mb="sm">
                    Interest by Category
                  </Text>

                  {/* flex: 1 allows it to fill space, minHeight guarantees it never gets squished */}
                  <Box style={{ flex: 1, minHeight: 220 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      {/* Added margin to prevent edge clipping */}
                      <PieChart margin={{ top: 10, bottom: 10 }}>
                        <Pie
                          data={
                            analytics?.categoryBreakdown?.length
                              ? analytics.categoryBreakdown.map((c) => ({
                                  name:
                                    c.name.length > 20
                                      ? `${c.name.slice(0, 20)}…`
                                      : c.name,
                                  value: c.value,
                                }))
                              : MOCK_CATEGORY_INTEREST
                          }
                          cx="50%"
                          cy="45%"
                          innerRadius={55} /* Scaled down slightly */
                          outerRadius={75} /* Scaled down slightly */
                          paddingAngle={5}
                          dataKey="value"
                          stroke="none" /* Removes the weird white border line between slices */
                        >
                          {(analytics?.categoryBreakdown?.length
                            ? analytics.categoryBreakdown
                            : MOCK_CATEGORY_INTEREST
                          ).map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={COLORS[index % COLORS.length]}
                            />
                          ))}
                        </Pie>
                        <RechartsTooltip />
                        <Legend
                          verticalAlign="bottom"
                          height={36}
                          iconType="circle"
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                </Card>
              </Stack>
            </Grid.Col>
          </Grid>
        </Tabs.Panel>
        {/* ==================== PROFILE & SAFETY TAB ==================== */}
        <Tabs.Panel value="profile" pt="xl">
          <Grid>
            {/* LEFT: Profile Section Popularity (Radar Chart) */}
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Card
                withBorder
                radius="md"
                p="xl"
                h="100%"
                style={{ display: "flex", flexDirection: "column" }}
              >
                <Text fw={700} mb="sm">
                  Personal History Access
                </Text>
                <Text size="sm" c="dimmed" mb="md">
                  Which parts of their profile users revisit the most.
                </Text>
                <Box style={{ flex: 1, minHeight: 250 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart
                      cx="50%"
                      cy="50%"
                      outerRadius="70%"
                      data={MOCK_PROFILE_SECTIONS}
                    >
                      <PolarGrid />
                      <PolarAngleAxis
                        dataKey="subject"
                        tick={{ fill: "#495057", fontSize: 12 }}
                      />
                      <PolarRadiusAxis
                        angle={30}
                        domain={[0, 15000]}
                        tick={false}
                      />
                      <Radar
                        name="Views"
                        dataKey="views"
                        stroke="#4F46E5"
                        fill="#4F46E5"
                        fillOpacity={0.5}
                      />
                      <RechartsTooltip />
                    </RadarChart>
                  </ResponsiveContainer>
                </Box>
              </Card>
            </Grid.Col>

            {/* RIGHT: Ticket Opens & Moderation */}
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Stack h="100%">
                {/* 1. Event Ticket Usage */}
                <Card
                  withBorder
                  radius="md"
                  p="xl"
                  className="flex-1"
                  style={{ display: "flex", flexDirection: "column" }}
                >
                  <Text fw={700} mb="sm">
                    Ticket QR Code Opens (Pre-Event)
                  </Text>
                  <Box style={{ flex: 1, minHeight: 150 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={MOCK_TICKET_OPENS}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="time" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <RechartsTooltip />
                        <Line
                          type="monotone"
                          dataKey="opens"
                          stroke="#10B981"
                          strokeWidth={3}
                          dot={{ r: 4 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </Card>

                {/* 2. Moderation / Account Investment Split */}
                <SimpleGrid cols={2} className="flex-1">
                  {/* Moderation Reports */}
                  <Card withBorder radius="md" p="md">
                    <Group gap="xs" mb="sm">
                      <ThemeIcon color="red" variant="light" size="sm">
                        <IconShieldCheck size={14} />
                      </ThemeIcon>
                      <Text fw={700} size="sm">
                        Moderation Reports
                      </Text>
                    </Group>
                    <Stack gap="xs" mt="md">
                      {(analytics?.moderationReasons?.length
                        ? analytics.moderationReasons.map((r) => ({
                            reason: r.reason,
                            count: r.count,
                          }))
                        : MOCK_REPORTS
                      ).map((report, idx) => (
                        <Group justify="space-between" key={idx}>
                          <Text size="xs" fw={500} lineClamp={1}>
                            {report.reason}
                          </Text>
                          <Badge color="red" variant="light">
                            {report.count}
                          </Badge>
                        </Group>
                      ))}
                      {analytics &&
                        analytics.moderationReasons?.length === 0 && (
                          <Text size="xs" c="dimmed">
                            No reports filed yet.
                          </Text>
                        )}
                    </Stack>
                  </Card>

                  {/* Profile Investment */}
                  <Card withBorder radius="md" p="md">
                    <Text fw={700} size="sm" mb="xs">
                      Account Investment
                    </Text>
                    <Text size="xs" c="dimmed" mb="md">
                      Users who edit their default profiles have 3x higher
                      retention.
                    </Text>

                    <Text size="xs" fw={600} mt="sm">
                      Avatar Uploads
                    </Text>
                    <Progress
                      value={analytics?.accountInvestment?.avatarPercent ?? 65}
                      color="blue"
                      size="sm"
                      mb="xs"
                    />

                    <Text size="xs" fw={600}>
                      Bio Updates
                    </Text>
                    <Progress
                      value={analytics?.accountInvestment?.bioPercent ?? 22}
                      color="grape"
                      size="sm"
                    />
                  </Card>
                </SimpleGrid>
              </Stack>
            </Grid.Col>
          </Grid>
        </Tabs.Panel>

        {/* ==================== GEOGRAPHIC REACH TAB ==================== */}
        <Tabs.Panel value="geography" pt="xl">
          <IndiaReachSection geography={analytics?.geography} />
        </Tabs.Panel>

        {/* ==================== OII (IP ENGINE) TAB ==================== */}
        <Tabs.Panel value="oii" pt="xl">
          {/* <Alert
            icon={<IconInfoCircle size={16} />}
            color="yellow"
            variant="light"
            radius="md"
            mb="lg"
          >
            Sample data — every chart, stat and AI insight below is
            illustrative placeholder data demonstrating the approved
            deliverable. Wire up live feeds before sharing externally.
          </Alert> */}

          <Tabs
            defaultValue="licensors"
            variant="pills"
            radius="xl"
            styles={{ tab: { fontWeight: 600 } }}
          >
            <Paper
              withBorder
              radius="xl"
              p={6}
              mb="xl"
              style={{ display: "inline-block", maxWidth: "100%" }}
            >
              <Tabs.List style={{ flexWrap: "wrap", rowGap: 6 }}>
                <Tabs.Tab
                  value="licensors"
                  leftSection={<IconMovie size={16} />}
                >
                  IP Licensors & Streaming
                </Tabs.Tab>
                <Tabs.Tab value="merch" leftSection={<IconShirt size={16} />}>
                  Merch & Apparel Brands
                </Tabs.Tab>
                <Tabs.Tab
                  value="sponsors"
                  leftSection={<IconSpeakerphone size={16} />}
                >
                  Non-Endemic Sponsors
                </Tabs.Tab>
                <Tabs.Tab
                  value="onboarding"
                  leftSection={<IconChecklist size={16} />}
                >
                  Onboarding Signals
                </Tabs.Tab>
              </Tabs.List>
            </Paper>

            {/* ============ PERSONA A: IP LICENSORS & STREAMING ============ */}
            <Tabs.Panel value="licensors">
              <Stack gap="lg">
                <Text size="sm" c="dimmed">
                  <b>The Decision:</b> Which upcoming anime series should we
                  license for the Indian market, and which legacy IPs justify
                  localized marketing budgets or theatrical releases?
                </Text>

                <Card withBorder radius="md" p="xl">
                  <Text fw={700} mb="xs">
                    Audience IP Migration
                  </Text>
                  <Text size="sm" c="dimmed" mb="lg">
                    Top 3 anime genres → interaction type (watch vs. read) →
                    hyped upcoming series. Shows exactly where fans of current
                    mainstream hits are migrating their attention next.
                  </Text>
                  <Box h={420}>
                    <ResponsiveContainer width="100%" height="100%">
                      <Sankey
                        data={MOCK_IP_MIGRATION_SANKEY}
                        node={<SankeyNode />}
                        nodePadding={36}
                        nodeWidth={14}
                        link={{ stroke: "#94A3B8", strokeOpacity: 0.35 }}
                        margin={{ top: 10, right: 130, bottom: 10, left: 10 }}
                      >
                        <RechartsTooltip />
                      </Sankey>
                    </ResponsiveContainer>
                  </Box>
                </Card>

                <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
                  <PersonaStatCard
                    icon={<IconTrendingUp size={18} />}
                    color="indigo"
                    eyebrow="The Next Breakout"
                    stat="68%"
                    description={
                      <>
                        of Shonen fans aged 18-24 in the OTA ecosystem are
                        actively hyped for <b>Solo Leveling S2</b>, representing
                        an immediate, highly-engaged initial viewership of{" "}
                        <b>7,900+</b> local fans.
                      </>
                    }
                  />
                  <AIInsightCard
                    title="Cross-IP Affinity Scoring"
                    model="Collaborative Filtering"
                    color="indigo"
                  >
                    Uses cosplay votes and crowdfunding ideas to predict hidden
                    affinities.{" "}
                    <b>
                      "Users who vote for Genshin Impact cosplay are 82% more
                      likely to request merch for Frieren."
                    </b>{" "}
                    Eliminates guesswork for licensing new seasons.
                  </AIInsightCard>
                </SimpleGrid>
              </Stack>
            </Tabs.Panel>

            {/* ============ PERSONA B: MERCH & APPAREL BRANDS ============ */}
            <Tabs.Panel value="merch">
              <Stack gap="lg">
                <Text size="sm" c="dimmed">
                  <b>The Decision:</b> Which character SKUs should we
                  manufacture next, and what inventory mix should we bring to
                  physical OTA events?
                </Text>

                <Card withBorder radius="md" p="xl">
                  <Text fw={700} mb="xs">
                    Spending Velocity by Fandom
                  </Text>
                  <Text size="sm" c="dimmed" mb="lg">
                    Cross-references past event/workshop spend against cosplay
                    vote interests — proves which fandoms actually have
                    disposable income. Naruto fans are numerous, but Evangelion
                    fans spend 3x more on premium workshops.
                  </Text>
                  <SpendCohortHeatmap data={MOCK_SPENDING_COHORT} />
                </Card>

                <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
                  <PersonaStatCard
                    icon={<IconUsers size={18} />}
                    color="grape"
                    eyebrow="High-Intent Collectors"
                    stat="42%"
                    description={
                      <>
                        of our Premium Ticketholders identify their primary
                        interaction as <b>"Collectibles"</b> — their #1
                        requested IP is <b>Evangelion</b>.
                      </>
                    }
                  />
                  <AIInsightCard
                    title="Merch Propensity Segments"
                    // model="Look-alike Modeling"
                    color="grape"
                  >
                    Identifies <b>"Whales."</b> By analyzing workshop attendance
                    history and checkout questionnaire data, the model flags the
                    top 15% of attendees most likely to convert on high-ticket
                    items (₹5,000+ statues) vs. volume items (₹500 tees).
                  </AIInsightCard>
                  <PosDependentInsightCard title="Basket Composition Analysis">
                    Determines attach rates —{" "}
                    <b>
                      "When someone buys a Demon Slayer shirt, there is a 40%
                      probability they also buy a blind box."
                    </b>
                  </PosDependentInsightCard>
                </SimpleGrid>
              </Stack>
            </Tabs.Panel>

            {/* ============ PERSONA C: NON-ENDEMIC SPONSORS ============ */}
            <Tabs.Panel value="sponsors">
              <Stack gap="lg">
                <Text size="sm" c="dimmed">
                  <b>The Decision:</b> Is the OTA audience the right demographic
                  for our brand, and what booth activation will yield the
                  highest ROI/lead generation?
                </Text>

                <Grid>
                  <Grid.Col span={{ base: 12, md: 7 }}>
                    <Card withBorder radius="md" p="xl" h="100%">
                      <Text fw={700} mb="xs">
                        Activation Engagement Funnel
                      </Text>
                      <Text size="sm" c="dimmed" mb="lg">
                        Total attendees → scanned QR at tech/FMCG stalls →
                        attended paid gaming/tech workshops. Proves OTA
                        attendees don't just walk past booths — they actively
                        scan, engage, and convert.
                      </Text>
                      <Box h={320}>
                        <ResponsiveContainer width="100%" height="100%">
                          <FunnelChart>
                            <RechartsTooltip cursor={{ fill: "transparent" }} />
                            <Funnel
                              dataKey="value"
                              data={MOCK_ACTIVATION_FUNNEL}
                              isAnimationActive
                            >
                              <LabelList
                                position="right"
                                fill="#000"
                                stroke="none"
                                dataKey="name"
                              />
                              <LabelList
                                position="center"
                                fill="#fff"
                                stroke="none"
                                dataKey="value"
                                fontWeight="bold"
                              />
                            </Funnel>
                          </FunnelChart>
                        </ResponsiveContainer>
                      </Box>
                    </Card>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 5 }}>
                    <PersonaStatCard
                      icon={<IconClock size={18} />}
                      color="teal"
                      eyebrow="The Ultimate Early Adopters"
                      stat="75%"
                      description={
                        <>
                          of QR scans at vendor stalls come from the{" "}
                          <b>18-24 male demographic</b>, with an average dwell
                          time of <b>6.5 minutes</b> per activation zone.
                        </>
                      }
                    />
                  </Grid.Col>
                </Grid>

                <Card withBorder radius="md" p="xl">
                  <Group gap="xs" mb="xs">
                    <ThemeIcon color="teal" variant="light" radius="xl">
                      <IconSparkles size={16} />
                    </ThemeIcon>
                    <Text fw={700} size="sm">
                      Footfall Heat Forecasting
                    </Text>
                    <Badge color="teal" variant="light" size="xs" ml="auto">
                      Spatial Forecasting
                    </Badge>
                  </Group>
                  <Text size="sm" c="dimmed" mb="lg">
                    Correlates historical QR scan timestamps with workshop
                    schedules to predict the exact peak hours for booth traffic
                    — letting sponsors staff booths and time free sample drops
                    perfectly.
                  </Text>
                  <Box h={280}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={MOCK_FOOTFALL_FORECAST}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="time" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <RechartsTooltip />
                        <ReferenceArea
                          x1="2pm"
                          x2="5pm"
                          fill="#0EA5E9"
                          fillOpacity={0.12}
                          label={{
                            value: "Predicted Peak",
                            position: "insideTop",
                            fill: "#0369A1",
                            fontSize: 12,
                            fontWeight: 700,
                          }}
                        />
                        <Area
                          type="monotone"
                          dataKey="scans"
                          name="QR Scans"
                          stroke="#0EA5E9"
                          fill="#BAE6FD"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </Box>
                </Card>
              </Stack>
            </Tabs.Panel>

            {/* ============ PERSONA D: ONBOARDING SIGNALS ============ */}
            <Tabs.Panel value="onboarding">
              <Stack gap="lg">
                <Group gap="xs">
                  <Text size="sm" c="dimmed">
                    <b>The Signal:</b> a 5-question onboarding MCQ, answered
                    once per user — the cheapest, highest-coverage input we have
                    feeding all three buyer personas above.
                  </Text>
                  {onboarding && (
                    <Badge color="teal" variant="light" size="xs">
                      Live data
                    </Badge>
                  )}
                </Group>

                <Group justify="space-between" align="center">
                  <Text fw={700}>What New Users Tell Us At Sign-Up</Text>
                  <Paper withBorder p="md" radius="md">
                    <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                      Total Responses Collected
                    </Text>
                    <Text fw={900} size="xl" className="ota-gradient-text">
                      {(
                        onboarding?.totalResponders ??
                        MOCK_ONBOARDING_TOTAL_RESPONSES
                      ).toLocaleString("en-IN")}
                    </Text>
                  </Paper>
                </Group>

                {(() => {
                  const findQ = (matchText) =>
                    onboarding?.questions?.find((q) =>
                      q.question.toLowerCase().includes(matchText),
                    )?.options;

                  const merchStyle =
                    findQ("merch style") || MOCK_ONBOARDING_MERCH_STYLE;
                  const tshirtSize =
                    findQ("t-shirt") || MOCK_ONBOARDING_TSHIRT_SIZE;
                  const gamingGenre =
                    findQ("play the most") || MOCK_ONBOARDING_GAMING_GENRE;
                  const gamingPlatform =
                    findQ("gaming platform") || MOCK_ONBOARDING_GAMING_PLATFORM;
                  const eventQuest =
                    findQ("main quest") || MOCK_ONBOARDING_EVENT_QUEST;

                  const genreKeyFor = (label) => {
                    const l = label.toLowerCase();
                    if (l.includes("gacha")) return "gachaRpg";
                    if (l.includes("shooter")) return "shooters";
                    if (l.includes("mobile esports")) return "mobileEsports";
                    if (l.includes("console")) return "consoleAAA";
                    return null;
                  };
                  const crossTab = onboarding?.platformGenreCrossTab?.length
                    ? onboarding.platformGenreCrossTab.map((row) => {
                        const out = {
                          platform: row.platform,
                          gachaRpg: 0,
                          shooters: 0,
                          mobileEsports: 0,
                          consoleAAA: 0,
                        };
                        for (const [genre, pct] of Object.entries(row.genres)) {
                          const key = genreKeyFor(genre);
                          if (key) out[key] = pct;
                        }
                        return out;
                      })
                    : MOCK_ONBOARDING_PLATFORM_GENRE_CROSS;

                  return (
                    <>
                      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg">
                        <OnboardingQuestionCard
                          icon={<IconShirt size={18} />}
                          color="grape"
                          question="What's your go-to anime merch style?"
                          feedsPersona="Merch Brands"
                          data={merchStyle}
                        />
                        <OnboardingQuestionCard
                          icon={<IconRuler size={18} />}
                          color="indigo"
                          question="Default fit (T-Shirt size)"
                          feedsPersona="Merch Brands · Inventory"
                          data={tshirtSize}
                          height={260}
                        />
                        <OnboardingQuestionCard
                          icon={<IconDeviceGamepad2 size={18} />}
                          color="blue"
                          question="Which of these do you play the most?"
                          feedsPersona="Non-Endemic Sponsors"
                          data={gamingGenre}
                        />
                        <OnboardingQuestionCard
                          icon={<IconDevices size={18} />}
                          color="teal"
                          question="Preferred gaming platform"
                          feedsPersona="Non-Endemic Sponsors"
                          data={gamingPlatform}
                        />
                      </SimpleGrid>

                      <OnboardingQuestionCard
                        icon={<IconCompass size={18} />}
                        color="orange"
                        question="What's your main quest at an OTA event?"
                        feedsPersona="Sponsors · IP Licensors"
                        data={eventQuest}
                        height={200}
                      />

                      <Card withBorder radius="md" p="xl">
                        <Group gap="xs" mb="xs">
                          <ThemeIcon color="blue" variant="light" radius="xl">
                            <IconSparkles size={16} />
                          </ThemeIcon>
                          <Text fw={700} size="sm">
                            Platform x Genre Cross-Tab
                          </Text>
                          <Badge
                            color="blue"
                            variant="light"
                            size="xs"
                            ml="auto"
                          >
                            Sponsor Targeting
                          </Badge>
                        </Group>
                        <Text size="sm" c="dimmed" mb="lg">
                          Cross-references the platform and genre onboarding
                          answers per user, per option selected.
                        </Text>
                        <PlatformGenreCrossTab data={crossTab} />
                      </Card>
                    </>
                  );
                })()}
              </Stack>
            </Tabs.Panel>
          </Tabs>
        </Tabs.Panel>
      </Tabs>
    </Box>
  );
}
